import { ApolloLink, createHttpLink, from } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient, fromPromise } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/browser';
import { get, isObject } from 'lodash';
import AppInfo from '../package.json';
import {
  APP,
  ROUTES,
  REFRESH_TOKEN as STORAGE_REFRESH_TOKEN,
  TOKEN,
  WORKSPACE
} from './common/constants';
import { messageContext } from './components/AppComponentContainer';
import history from './historyData';
import { REFRESH_TOKEN } from './modules/auth/graphql/Mutations';

let disableToastTimeout = null;

let isRefreshing = false;
let pendingRequests = [];
let newAccessToken;

const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

export const cacheData = new InMemoryCache();

const httpLink = createHttpLink({
  uri: process.env?.REACT_APP_SERVER_GRAPH_URL
});

export const toast = ({ message: content, type }) => {
  messageContext?.destroy();
  switch (type) {
    case 'info':
      messageContext?.info(content);
      break;
    case 'success':
      messageContext?.success(content);
      break;
    case 'warning':
      messageContext?.warning(content);
      break;
    case 'error':
      messageContext?.error(content);
      break;
    default:
      break;
  }
};

const authLink = setContext((ctx, { headers }) => {
  // eslint-disable-next-line no-undef
  const userToken = localStorage.getItem(TOKEN);
  // eslint-disable-next-line no-undef
  const workspaceID = JSON.parse(localStorage.getItem(WORKSPACE))?.value;
  // eslint-disable-next-line no-undef
  const appId = JSON.parse(localStorage.getItem(APP))?.value;
  let newHeaders = headers || {};

  newHeaders = {
    ...newHeaders,
    Authorization: userToken ? `Bearer ${userToken}` : '',
    ...(workspaceID && { 'x-workspace-id': workspaceID }),
    ...(appId && { 'x-app-id': appId })
  };

  return {
    headers: newHeaders
  };
});

const httpAuthServerLink = createHttpLink({
  uri: process.env.REACT_APP_AUTH_GRAPH_URL
});

const getNewToken = async (refreshToken) => {
  if (!refreshToken) {
    throw new Error('Please provide refresh token');
  }
  try {
    const client = new ApolloClient({
      link: httpAuthServerLink,
      cache: new InMemoryCache()
    });

    const { data, errors } = await client.mutate({
      mutation: REFRESH_TOKEN,
      variables: {
        data: { refreshToken }
      }
    });

    if (data?.refreshTokenAdmin?.accessToken)
      return data.refreshTokenAdmin.accessToken;

    throw new Error(errors?.[0].message ?? 'Something went wrong!');
  } catch (error) {
    if (error instanceof Error) throw new Error(error.message);
    throw new Error('Something went wrong!');
  }
};

const responseMessageLink = new ApolloLink((operation, forward) =>
  forward(operation)?.map((response) => {
    const { data } = response;

    if (
      data &&
      isObject(data) &&
      Object?.keys(data)?.length > 0 &&
      data?.[`${Object?.keys(data)?.[0]}`]?.message
    ) {
      if (Object?.keys(data)?.[0] === 'forgotUserPassword') {
        if (data?.[`${Object?.keys(data)?.[0]}`]?.status !== 'ERROR') {
          setTimeout(() => {
            toast({
              message:
                data?.[`${Object?.keys(data)?.[0]}`]?.message ||
                'Operation successful',
              type: 'success'
            });
          }, 1000);
        }
      } else {
        setTimeout(() => {
          const oResponse = data?.[`${Object?.keys(data)?.[0]}`];

          if (!response) {
            return;
          }

          toast({
            message: oResponse?.message || 'Operation successful',
            type: oResponse?.status === 'ERROR' ? 'error' : 'success'
          });
        }, 1000);
      }
    }
    return response;
  })
);

const errorLink = onError((options) => {
  const { graphQLErrors, networkError, response, operation, forward } = options;

  if (networkError?.statusCode === 405) {
    if (disableToastTimeout) {
      clearTimeout(disableToastTimeout);
    }

    disableToastTimeout = setTimeout(() => {
      if (networkError?.result?.message) {
        toast({
          message: networkError?.result?.message,
          type: 'error'
        });
      }
    }, 200);

    history?.replace(ROUTES?.LOGOUT);
    return;
  }

  if (graphQLErrors?.length > 0) {
    const isForBidden =
      get(graphQLErrors?.[0], 'extensions.code') === 'FORBIDDEN';
    const isSessionExpired =
      get(graphQLErrors?.[0], 'extensions.code') === 'SESSION_EXPIRED';

    if (!isForBidden && !isSessionExpired) {
      setTimeout(() => {
        toast({
          message: graphQLErrors?.[0]?.message,
          type: 'error'
        });
      }, 1000);
    }
    if (isSessionExpired) {
      let forward$;
      if (!isRefreshing) {
        isRefreshing = true;
        // eslint-disable-next-line no-undef
        const refreshToken = localStorage.getItem(STORAGE_REFRESH_TOKEN);
        forward$ = fromPromise(
          getNewToken(refreshToken)
            // eslint-disable-next-line no-loop-func
            .then((newToken) => {
              // eslint-disable-next-line no-undef
              localStorage.setItem(TOKEN, newToken);
              resolvePendingRequests();
              newAccessToken = newToken;
              isRefreshing = false;
              return newToken;
            })
            // eslint-disable-next-line no-loop-func
            .catch((apiError) => {
              pendingRequests = [];
              history?.replace(ROUTES.LOGOUT);
              toast({
                message:
                  apiError instanceof Error
                    ? apiError.message
                    : 'Something went wrong!',
                type: 'error'
              });
              return null;
            })
            // eslint-disable-next-line no-loop-func
            .finally(() => {
              isRefreshing = false;
            })
        ).filter(Boolean);
      } else {
        forward$ = fromPromise(
          // eslint-disable-next-line no-loop-func
          new Promise((resolve) => {
            pendingRequests.push(() => resolve(null));
          })
        );
      }
      // eslint-disable-next-line no-loop-func
      return forward$.flatMap(() => {
        const oldHeaders = operation.getContext().headers;

        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${newAccessToken}`
          }
        });

        return forward(operation);
      });
    }
  } else {
    setTimeout(() => {
      toast({
        message: 'Something went wrong!',
        type: 'error'
      });
    }, 1000);
  }

  if (response) {
    response?.errors?.map((error) => {
      const { message: errorMessage, locations, path, extensions } = error;

      // Enable when sentry integrated
      Sentry?.captureException(
        new Error(
          `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`
        )
      );

      if (extensions?.code === 'WORKSPACE_NOT_FOUND') {
        // eslint-disable-next-line no-undef
        window.location.reload();
      }

      if (extensions?.code === 'FORBIDDEN') {
        history?.replace('/access-denied');
      }

      if (
        extensions?.code === 'UNAUTHENTICATED' ||
        extensions?.code === 405 ||
        extensions?.code === 'INVALID_TOKEN' ||
        extensions?.exception?.name === 'JsonWebTokenError'
      ) {
        history?.replace(ROUTES?.LOGOUT);
      }

      // eslint-disable-next-line no-console
      return console?.log(
        `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (networkError) {
    // eslint-disable-next-line no-console
    console?.log(`[Network error]: ${networkError}`);
    Sentry?.captureException(new Error(`[Network error]: ${networkError}`));
  }
});

const client = new ApolloClient({
  cache: cacheData,
  link: from([responseMessageLink, errorLink, authLink, httpLink]),
  name: 'CMS-Web',
  version: AppInfo?.version
});

const authClient = new ApolloClient({
  cache: cacheData,
  link: from([responseMessageLink, errorLink, authLink, httpAuthServerLink])
});

export default client;

export { authClient };
